import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { keccak256, encodePacked } from 'viem'
import { IS_LIVE } from '@/lib/contracts'
import v3VaultAbi from '@/lib/abis/V3Vault.json'
import { POLL_SLOW, toNumber } from '@/lib/utils'

const Q128 = 1n << 128n
const MASK256 = (1n << 256n) - 1n
const POOL_ABI = [
  { type: 'function', name: 'slot0', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint160' }, { type: 'int24' }, { type: 'uint16' }, { type: 'uint16' }, { type: 'uint16' }, { type: 'uint8' }, { type: 'bool' }] },
  { type: 'function', name: 'feeGrowthGlobal0X128', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'feeGrowthGlobal1X128', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'ticks', stateMutability: 'view', inputs: [{ type: 'int24' }], outputs: [{ type: 'uint128' }, { type: 'int128' }, { type: 'uint256' }, { type: 'uint256' }, { type: 'int56' }, { type: 'uint160' }, { type: 'uint32' }, { type: 'bool' }] },
  { type: 'function', name: 'positions', stateMutability: 'view', inputs: [{ type: 'bytes32' }], outputs: [{ type: 'uint128' }, { type: 'uint256' }, { type: 'uint256' }, { type: 'uint128' }, { type: 'uint128' }] },
]

/// LIVE uncollected Uniswap fees a vault's position has EARNED but not yet compounded, computed off
/// the pool's fee-growth accumulators (the standard V3 formula) - NOT derived from NAV history and
/// NOT gated on compound's price guard, so it reflects real trading the instant it happens. This is
/// the "fees accruing now" signal that complements the trailing realised figure from
/// useVaultNavHistory. Only V3 vaults holding a position (tvlUsd > 0) are read; an empty vault owes
/// nothing.
export function useVaultPendingFees(vaults) {
  const active = useMemo(() => (vaults ?? []).filter((v) => v.venue !== 'v4' && v.tvlUsd > 0 && !v.loading), [vaults])

  // Stage 1: pool address + ranges per active vault.
  const { data: baseReads } = useReadContracts({
    query: { enabled: IS_LIVE && active.length > 0, refetchInterval: POLL_SLOW },
    contracts: active.flatMap((v) => [
      { address: v.address, abi: v3VaultAbi, functionName: 'pool' },
      { address: v.address, abi: v3VaultAbi, functionName: 'ranges' },
    ]),
  })
  const meta = useMemo(() => active.map((v, i) => ({
    vault: v,
    pool: baseReads?.[i * 2]?.result,
    ranges: (baseReads?.[i * 2 + 1]?.result ?? []).filter((r) => (r.weightBps ?? r[2] ?? 0) >= 0),
  })), [active, baseReads])

  // Stage 2: for each vault, pool globals + per-range tick/position reads. Flattened with an index map.
  const { contracts, layout } = useMemo(() => {
    const c = [], lay = []
    for (const m of meta) {
      if (!m.pool) { lay.push(null); continue }
      const start = c.length
      c.push(
        { address: m.pool, abi: POOL_ABI, functionName: 'slot0' },
        { address: m.pool, abi: POOL_ABI, functionName: 'feeGrowthGlobal0X128' },
        { address: m.pool, abi: POOL_ABI, functionName: 'feeGrowthGlobal1X128' },
      )
      const rangeIdx = []
      for (const r of m.ranges) {
        const tl = Number(r.tickLower ?? r[0]), tu = Number(r.tickUpper ?? r[1])
        const key = keccak256(encodePacked(['address', 'int24', 'int24'], [m.vault.address, tl, tu]))
        rangeIdx.push({ tl, tu, at: c.length })
        c.push(
          { address: m.pool, abi: POOL_ABI, functionName: 'ticks', args: [tl] },
          { address: m.pool, abi: POOL_ABI, functionName: 'ticks', args: [tu] },
          { address: m.pool, abi: POOL_ABI, functionName: 'positions', args: [key] },
        )
      }
      lay.push({ start, rangeIdx })
    }
    return { contracts: c, layout: lay }
  }, [meta])

  const { data: feeReads } = useReadContracts({
    query: { enabled: IS_LIVE && contracts.length > 0, refetchInterval: POLL_SLOW },
    contracts,
  })

  const byVault = useMemo(() => {
    const out = {}
    meta.forEach((m, i) => {
      const lay = layout[i]
      if (!lay || !feeReads) return
      const tick = Number(feeReads[lay.start]?.result?.[1] ?? 0n)
      const fg0 = feeReads[lay.start + 1]?.result ?? 0n
      const fg1 = feeReads[lay.start + 2]?.result ?? 0n
      let p0 = 0n, p1 = 0n
      for (const ri of lay.rangeIdx) {
        const lo = feeReads[ri.at]?.result, hi = feeReads[ri.at + 1]?.result, pos = feeReads[ri.at + 2]?.result
        if (!lo || !hi || !pos || (pos[0] ?? 0n) === 0n) continue
        const liq = pos[0], fgi0Last = pos[1], fgi1Last = pos[2], owed0 = pos[3], owed1 = pos[4]
        const loFg0 = lo[2], loFg1 = lo[3], hiFg0 = hi[2], hiFg1 = hi[3]
        const below0 = tick >= ri.tl ? loFg0 : (fg0 - loFg0) & MASK256
        const above0 = tick < ri.tu ? hiFg0 : (fg0 - hiFg0) & MASK256
        const below1 = tick >= ri.tl ? loFg1 : (fg1 - loFg1) & MASK256
        const above1 = tick < ri.tu ? hiFg1 : (fg1 - hiFg1) & MASK256
        const inside0 = (fg0 - below0 - above0) & MASK256
        const inside1 = (fg1 - below1 - above1) & MASK256
        p0 += owed0 + (liq * ((inside0 - fgi0Last) & MASK256)) / Q128
        p1 += owed1 + (liq * ((inside1 - fgi1Last) & MASK256)) / Q128
      }
      // Price each leg off the vault's own per-unit USD (priced / amount from the list).
      const t0 = m.vault.token0, t1 = m.vault.token1
      const price0 = t0?.amount > 0 ? (t0.priced ?? 0) / t0.amount : 0
      const price1 = t1?.amount > 0 ? (t1.priced ?? 0) / t1.amount : 0
      const usd = toNumber(p0, t0?.decimals ?? 18) * price0 + toNumber(p1, t1?.decimals ?? 18) * price1
      out[m.vault.address.toLowerCase()] = { amount0: p0, amount1: p1, usd }
    })
    return out
  }, [meta, layout, feeReads])

  return {
    pendingFeesUsdFor: (address) => (address ? byVault[address.toLowerCase()]?.usd ?? null : null),
    pendingFeesFor: (address) => (address ? byVault[address.toLowerCase()] ?? null : null),
  }
}
